const API_URL = import.meta.env.VITE_API_URL || '';

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

function getStoredToken() {
  return localStorage.getItem('ourday_token');
}

function authHeaders(token) {
  const t = token || getStoredToken();
  return t ? { 'Authorization': `Bearer ${t}` } : {};
}

// ---------------------------------------------------------------------------
// New standalone auth methods
// ---------------------------------------------------------------------------

export async function registerUser(email, password, name) {
  const res = await fetch(`${API_URL}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, name }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Registration failed' }));
    throw new Error(err.error || err.message || 'Registration failed');
  }
  return res.json();
}

export async function loginUser(email, password) {
  const body = {};
  if (email) body.email = email;
  if (password !== undefined) body.password = password;

  const res = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Login failed' }));
    throw new Error(err.error || err.message || 'Login failed');
  }
  return res.json();
}

export async function getMe(token) {
  const res = await fetch(`${API_URL}/api/auth/me`, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to fetch user' }));
    throw new Error(err.error || err.message || 'Failed to fetch user');
  }
  return res.json();
}

export async function updateMe(token, data) {
  const res = await fetch(`${API_URL}/api/auth/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to update profile' }));
    throw new Error(err.error || err.message || 'Failed to update profile');
  }
  return res.json();
}

export async function forgotPassword(email) {
  const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

export async function resetPassword(token, newPassword) {
  const res = await fetch(`${API_URL}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Reset failed');
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Weddings
// ---------------------------------------------------------------------------

export async function listWeddings(token) {
  const res = await fetch(`${API_URL}/api/weddings`, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to list weddings');
  return res.json();
}

export async function createWedding(token, data) {
  const res = await fetch(`${API_URL}/api/weddings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to create wedding' }));
    throw new Error(err.error || err.message || 'Failed to create wedding');
  }
  return res.json();
}

export async function getWedding(token, weddingId) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}`, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to fetch wedding');
  return res.json();
}

export async function updateWedding(token, weddingId, content) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify(content),
  });
  if (!res.ok) throw new Error('Failed to update wedding');
  return res.json();
}

export async function deleteWedding(token, weddingId) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}`, {
    method: 'DELETE',
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to delete wedding');
  return res.json();
}

export async function publishWedding(token, weddingId) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/publish`, {
    method: 'POST',
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to publish wedding');
  return res.json();
}

// ---------------------------------------------------------------------------
// Wedding-scoped guests
// ---------------------------------------------------------------------------

export async function getWeddingGuests(token, weddingId, { search, status } = {}) {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (status) params.append('status', status);

  const qs = params.toString();
  const url = `${API_URL}/api/weddings/${weddingId}/guests${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to fetch wedding guests');
  return res.json();
}

export async function addWeddingGuest(token, weddingId, data) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/guests`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to add guest');
  return res.json();
}

export async function getWeddingGuestStats(token, weddingId) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/guests/stats`, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to fetch wedding guest stats');
  return res.json();
}

export async function updateWeddingGuest(token, weddingId, guestId, data) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/guests/${guestId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update wedding guest');
  return res.json();
}

export async function uploadWeddingGuestReceipt(token, weddingId, guestId, file) {
  const formData = new FormData();
  formData.append('receipt', file);

  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/guests/${guestId}/receipt`, {
    method: 'POST',
    headers: { ...authHeaders(token) },
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to upload receipt');
  return res.json();
}

export async function bulkImportWeddingGuests(token, weddingId, guests) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/guests/bulk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify({ guests }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to import guests' }));
    throw new Error(err.error || err.message || 'Failed to import guests');
  }
  return res.json();
}

export async function deleteWeddingGuest(token, weddingId, guestId) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/guests/${guestId}`, {
    method: 'DELETE',
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to delete wedding guest');
  return res.json();
}

// ---------------------------------------------------------------------------
// Wedding-scoped media (R2 prefix isolation)
// ---------------------------------------------------------------------------

export async function listWeddingImages(token, weddingId, limit = 50, cursor) {
  const params = new URLSearchParams();
  if (limit) params.append('limit', String(limit));
  if (cursor) params.append('cursor', cursor);
  const qs = params.toString();
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/images${qs ? `?${qs}` : ''}`, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to list wedding images');
  return res.json();
}

export async function uploadWeddingImage(token, weddingId, file) {
  const formData = new FormData();
  formData.append('image', file);
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/images`, {
    method: 'POST',
    headers: { ...authHeaders(token) },
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to upload wedding image');
  return res.json();
}

export async function deleteWeddingImage(token, weddingId, filename) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/images/${filename}`, {
    method: 'DELETE',
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to delete wedding image');
  return res.json();
}

// ---------------------------------------------------------------------------
// Public wedding (no auth required)
// ---------------------------------------------------------------------------

export async function getPublicWedding(slug) {
  const res = await fetch(`${API_URL}/api/w/${slug}`);
  if (!res.ok) throw new Error('Failed to fetch wedding');
  return res.json();
}

export async function getPublicWeddingByDomain(domain) {
  const res = await fetch(`${API_URL}/api/w/by-domain/${encodeURIComponent(domain)}`);
  if (!res.ok) throw new Error('Wedding not found');
  return res.json();
}

export async function updateWeddingSubdomain(token, weddingId, subdomain) {
  const res = await fetch(`${API_URL}/api/weddings/${weddingId}/subdomain`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ subdomain }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update subdomain');
  return data;
}

export async function checkSubdomainAvailability(token, weddingId, subdomain) {
  const res = await fetch(
    `${API_URL}/api/weddings/${weddingId}/subdomain/check?subdomain=${encodeURIComponent(subdomain)}`,
    { headers: { ...authHeaders(token) } }
  );
  if (!res.ok) throw new Error('Failed to check subdomain');
  return res.json();
}

export async function submitPublicRsvp(slug, data) {
  const res = await fetch(`${API_URL}/api/w/${slug}/rsvp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to submit RSVP' }));
    throw new Error(err.error || err.message || 'Failed to submit RSVP');
  }
  return res.json();
}

export async function getPublicWishes(slug) {
  const res = await fetch(`${API_URL}/api/w/${slug}/wishes`);
  if (!res.ok) return { wishes: [] };
  return res.json();
}

// ---------------------------------------------------------------------------
// Admin (superadmin)
// ---------------------------------------------------------------------------

export async function getAdminStats(token) {
  const res = await fetch(`${API_URL}/api/admin/stats`, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to fetch admin stats');
  return res.json();
}

export async function getAdminWeddings(token, params = {}) {
  const query = new URLSearchParams();
  if (params.search) query.append('search', params.search);
  if (params.limit) query.append('limit', params.limit.toString());
  if (params.offset) query.append('offset', params.offset.toString());
  if (params.status) query.append('status', params.status);

  const qs = query.toString();
  const url = `${API_URL}/api/admin/weddings${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to fetch admin weddings');
  return res.json();
}

export async function getAdminUsers(token, params = {}) {
  const query = new URLSearchParams();
  if (params.search) query.append('search', params.search);
  if (params.limit) query.append('limit', params.limit.toString());
  if (params.offset) query.append('offset', params.offset.toString());

  const qs = query.toString();
  const url = `${API_URL}/api/admin/users${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to fetch admin users');
  return res.json();
}

export async function getAdminRsvps(token, params = {}) {
  const query = new URLSearchParams();
  if (params.search) query.append('search', params.search);
  if (params.limit) query.append('limit', params.limit.toString());
  if (params.offset) query.append('offset', params.offset.toString());
  if (params.weddingId) query.append('weddingId', params.weddingId);

  const qs = query.toString();
  const url = `${API_URL}/api/admin/rsvps${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, {
    headers: { ...authHeaders(token) },
  });
  if (!res.ok) throw new Error('Failed to fetch admin RSVPs');
  return res.json();
}

// ---------------------------------------------------------------------------
// Legacy api object -- ALL existing methods preserved
// ---------------------------------------------------------------------------

export const api = {
  getContent: async () => {
    const res = await fetch(`${API_URL}/api/content`);
    if (!res.ok) throw new Error('Failed to fetch content');
    return res.json();
  },

  updateContent: async (content, token) => {
    const res = await fetch(`${API_URL}/api/content`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(content),
    });
    if (!res.ok) throw new Error('Failed to update content');
    return res.json();
  },

  login: async (password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) throw new Error('Invalid password');
    return res.json();
  },

  uploadImage: async (file, token) => {
    const formData = new FormData();
    formData.append('image', file);

    const res = await fetch(`${API_URL}/api/uploads/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to upload image' }));
      throw new Error(error.error || 'Failed to upload image');
    }

    return res.json();
  },

  listImages: async (token, limit = 50, cursor = null) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor);

    const res = await fetch(`${API_URL}/api/uploads/images?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error('Failed to list images');
    return res.json();
  },

  deleteImage: async (filename, token) => {
    const res = await fetch(`${API_URL}/api/uploads/image/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error('Failed to delete image');
    return res.json();
  },

  uploadAudio: async (file, token) => {
    const formData = new FormData();
    formData.append('audio', file);

    const res = await fetch(`${API_URL}/api/audio/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to upload audio' }));
      throw new Error(error.error || 'Failed to upload audio');
    }

    return res.json();
  },

  deleteAudio: async (filename, token) => {
    const res = await fetch(`${API_URL}/api/audio/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error('Failed to delete audio');
    return res.json();
  },

  // RSVP
  submitRsvp: async (formData) => {
    const res = await fetch(`${API_URL}/api/rsvp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to submit RSVP' }));
      throw new Error(error.error || 'Failed to submit RSVP');
    }
    return res.json();
  },

  getGuests: async (token, params = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.append('search', params.search);
    if (params.status) query.append('status', params.status);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());

    const res = await fetch(`${API_URL}/api/rsvp?${query}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch guests');
    return res.json();
  },

  getGuestStats: async (token) => {
    const res = await fetch(`${API_URL}/api/rsvp/stats`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch guest stats');
    return res.json();
  },

  updateGuest: async (id, data, token) => {
    const res = await fetch(`${API_URL}/api/rsvp/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update guest');
    return res.json();
  },

  deleteGuest: async (id, token) => {
    const res = await fetch(`${API_URL}/api/rsvp/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to delete guest');
    return res.json();
  },

  // WhatsApp
  getWhatsappSessions: async (token) => {
    const res = await fetch(`${API_URL}/api/whatsapp/sessions`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch WhatsApp sessions');
    return res.json();
  },

  sendWhatsappMessage: async (phone, message, token, session = 'default') => {
    const res = await fetch(`${API_URL}/api/whatsapp/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ phone, message, session }),
    });
    if (!res.ok) throw new Error('Failed to send WhatsApp message');
    return res.json();
  },

  // Receipt upload
  uploadReceipt: async (guestId, file, token) => {
    const formData = new FormData();
    formData.append('receipt', file);

    const res = await fetch(`${API_URL}/api/rsvp/${guestId}/receipt`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to upload receipt' }));
      throw new Error(error.error || 'Failed to upload receipt');
    }
    return res.json();
  },

  // WhatsApp key management (per-session API keys)
  getWhatsappKeys: async (token) => {
    const res = await fetch(`${API_URL}/api/whatsapp/keys`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch WhatsApp keys');
    return res.json();
  },

  createWhatsappKey: async (session, token) => {
    const res = await fetch(`${API_URL}/api/whatsapp/keys`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ session }),
    });
    if (!res.ok) throw new Error('Failed to create WhatsApp key');
    return res.json();
  },

  deleteWhatsappKey: async (keyId, token) => {
    const res = await fetch(`${API_URL}/api/whatsapp/keys/${keyId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to delete WhatsApp key');
    return res.json();
  },

  // WhatsApp session management
  startWhatsappSession: async (token, session = 'default', weddingId) => {
    const res = await fetch(`${API_URL}/api/whatsapp/sessions/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ session, weddingId }),
    });
    if (!res.ok) throw new Error('Failed to start WhatsApp session');
    return res.json();
  },

  stopWhatsappSession: async (token, session = 'default') => {
    const res = await fetch(`${API_URL}/api/whatsapp/sessions/stop`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ session }),
    });
    if (!res.ok) throw new Error('Failed to stop WhatsApp session');
    return res.json();
  },

  getWhatsappQR: async (token, session = 'default') => {
    const res = await fetch(`${API_URL}/api/whatsapp/sessions/${session}/auth/qr`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get QR code');
    return res.json();
  },

  getWhatsappMe: async (token, session = 'default') => {
    const res = await fetch(`${API_URL}/api/whatsapp/sessions/${session}/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get session info');
    return res.json();
  },

  logoutWhatsappSession: async (token, session = 'default') => {
    const res = await fetch(`${API_URL}/api/whatsapp/sessions/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ session }),
    });
    if (!res.ok) throw new Error('Failed to logout WhatsApp session');
    return res.json();
  },

  // PayPal
  createPayPalSubscription: async (planId, billingCycle, token, weddingId, currency = 'USD') => {
    const res = await fetch(`${API_URL}/api/paypal/create-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ planId, billingCycle, weddingId, currency }),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to create subscription' }));
      throw new Error(error.error || 'Failed to create subscription');
    }
    return res.json();
  },

  cancelPayPalSubscription: async (subscriptionId, token) => {
    const res = await fetch(`${API_URL}/api/paypal/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ subscriptionId }),
    });
    if (!res.ok) throw new Error('Failed to cancel subscription');
    return res.json();
  },

  getSubscriptionStatus: async (token, weddingId) => {
    const qs = weddingId ? `?weddingId=${weddingId}` : '';
    const res = await fetch(`${API_URL}/api/paypal/subscription-status${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get subscription status');
    return res.json();
  },

  getInvoices: async (token, weddingId) => {
    const qs = weddingId ? `?weddingId=${weddingId}` : '';
    const res = await fetch(`${API_URL}/api/paypal/invoices${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get invoices');
    return res.json();
  },

  // Instagram
  getInstagramAuthUrl: async (token, weddingId) => {
    const qs = weddingId ? `?weddingId=${weddingId}` : '';
    const res = await fetch(`${API_URL}/api/instagram/auth-url${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to get auth URL' }));
      throw new Error(error.error || 'Failed to get auth URL');
    }
    return res.json();
  },

  instagramCallback: async (code, state, token) => {
    const res = await fetch(`${API_URL}/api/instagram/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ code, state }),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to connect Instagram' }));
      throw new Error(error.error || 'Failed to connect Instagram');
    }
    return res.json();
  },

  disconnectInstagram: async (token, weddingId) => {
    const res = await fetch(`${API_URL}/api/instagram/disconnect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ weddingId }),
    });
    if (!res.ok) throw new Error('Failed to disconnect Instagram');
    return res.json();
  },

  getInstagramUser: async (token, weddingId) => {
    const qs = weddingId ? `?weddingId=${weddingId}` : '';
    const res = await fetch(`${API_URL}/api/instagram/user${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get Instagram info');
    return res.json();
  },

  // Domain management
  checkDomainAvailability: async (token, domain) => {
    const res = await fetch(`${API_URL}/api/domains/check?domain=${encodeURIComponent(domain)}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to check domain' }));
      throw new Error(error.error || 'Failed to check domain');
    }
    return res.json();
  },

  registerDomain: async (token, domain, weddingId) => {
    const res = await fetch(`${API_URL}/api/domains/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ domain, weddingId }),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to register domain' }));
      throw new Error(error.error || 'Failed to register domain');
    }
    return res.json();
  },

  // Weddings (legacy object methods)
  listWeddings: async (token) => {
    const res = await fetch(`${API_URL}/api/weddings`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch weddings');
    return res.json();
  },

  createWedding: async (token, data) => {
    const res = await fetch(`${API_URL}/api/weddings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to create wedding' }));
      throw new Error(error.error || 'Failed to create wedding');
    }
    return res.json();
  },

  getWedding: async (token, id) => {
    const res = await fetch(`${API_URL}/api/weddings/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch wedding');
    return res.json();
  },

  deleteWedding: async (token, id) => {
    const res = await fetch(`${API_URL}/api/weddings/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to delete wedding');
    return res.json();
  },

  publishWedding: async (token, id) => {
    const res = await fetch(`${API_URL}/api/weddings/${id}/publish`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to publish wedding');
    return res.json();
  },

  unpublishWedding: async (token, id) => {
    const res = await fetch(`${API_URL}/api/weddings/${id}/unpublish`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to unpublish wedding');
    return res.json();
  },

  getPublicWedding: async (slug) => {
    const res = await fetch(`${API_URL}/api/w/${slug}`);
    if (!res.ok) throw new Error('Wedding not found');
    return res.json();
  },

  // Admin (superadmin)
  getAdminStats: async (token) => {
    const res = await fetch(`${API_URL}/api/admin/stats`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch admin stats');
    return res.json();
  },

  getAdminWeddings: async (token, params = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.append('search', params.search);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset !== undefined) query.append('offset', params.offset.toString());
    if (params.status) query.append('status', params.status);
    const qs = query.toString();
    const res = await fetch(`${API_URL}/api/admin/weddings${qs ? '?' + qs : ''}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch admin weddings');
    return res.json();
  },

  getAdminSubscriptions: async (token, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const res = await fetch(`${API_URL}/api/admin/subscriptions${qs ? '?' + qs : ''}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch subscriptions');
    return res.json();
  },

  // Admin actions
  anonymizeWedding: async (token, weddingId) => {
    const res = await fetch(`${API_URL}/api/admin/weddings/${weddingId}/anonymize`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to anonymize wedding' }));
      throw new Error(error.error || 'Failed to anonymize wedding');
    }
    return res.json();
  },

  // Migrations
  createRsvpTable: async (token) => {
    const res = await fetch(`${API_URL}/api/migrate/create-rsvp-table`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to create RSVP table');
    return res.json();
  },

  runMigration: async (endpoint, token) => {
    const res = await fetch(`${API_URL}/api/migrate/${endpoint}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Migration failed');
    return res.json();
  },
};
